// ---------------------------------------------------------------------------
// Canonical cuisines
// ---------------------------------------------------------------------------
// The `cuisine` field on a restaurant is free text typed by the owner (or by an
// admin), so the database holds things like "Italian", "Modern Italian" and
// "Italian Farm-to-Table" — three unrelated values as far as a filter is
// concerned. The listings filters need a closed set to group by.
//
// Rather than block the filters on a data migration, this maps each free-text
// value onto one or more canonical labels at read time. Once cuisine becomes a
// dropdown at onboarding and the existing rows are tidied, `canonicalCuisines`
// becomes a straight passthrough and this file stays as the source list for
// that dropdown.
//
// A restaurant whose cuisine matches nothing here keeps its own text as its
// label (see `cuisineLabels`), so it is still filterable — it just gets a chip
// of its own until the data is cleaned up.
// ---------------------------------------------------------------------------

// Longest-first within each overlapping group: 'Middle Eastern' has to be
// tested before 'Eastern'-style substrings, and 'Latin American' before
// 'American', or the broader label swallows the specific one.
export const CUISINES = [
  "Bar",
  "Nightclub",
  "Lounge",
  "Pub",
  "Sports Bar",
  "Cocktail Bar",
  "Wine Bar",
  "Brewery",
  "Rooftop Bar",
  "Dive Bar",
  "Karaoke Bar",
  "Hookah Lounge",
  "Live Music",
  "Dance Club"
];

// A few things owners write that don't contain the canonical word.
const ALIASES = { 'night club': 'Nightclub', 'cocktails': 'Cocktail Bar', 'taproom': 'Brewery', 'karaoke': 'Karaoke Bar', 'rooftop': 'Rooftop Bar', 'hookah': 'Hookah Lounge', 'dance': 'Dance Club' };

// One glyph per canonical cuisine, for the filter rail.
//
// Emoji rather than an icon set: the rail needs a mark for whatever cuisines
// happen to be listed, that set changes as restaurants are onboarded, and
// every platform already ships these. A bespoke SVG per cuisine would be a
// nicer artifact and a worse trade — it adds a design task to every new
// cuisine, and the first one nobody draws leaves a hole in the rail.
const CUISINE_ICONS = {
  "Bar": "🍺",
  "Nightclub": "🪩",
  "Lounge": "🛋",
  "Pub": "🍻",
  "Sports Bar": "🏈",
  "Cocktail Bar": "🍸",
  "Wine Bar": "🍷",
  "Brewery": "🍺",
  "Rooftop Bar": "🌃",
  "Dive Bar": "🍺",
  "Karaoke Bar": "🎤",
  "Hookah Lounge": "💨",
  "Live Music": "🎵",
  "Dance Club": "💃"
};

// Falls back to a plate for the free-text labels that aren't canonical yet, so
// an unrecognised cuisine still gets a mark and the chips stay the same shape.
export function cuisineIcon(label) {
  return CUISINE_ICONS[label] || '\u{1f378}';
}

function normalize(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// Every canonical label that applies to one free-text cuisine string.
// "Italian Pizzeria" -> ['Italian', 'Pizza'].
export function canonicalCuisines(text) {
  const n = normalize(text);
  if (!n) return [];
  const found = [];
  for (const c of CUISINES) {
    if (n.includes(normalize(c)) && !found.includes(c) && !(c === 'Bar' && CUISINES.some(specific => specific !== 'Bar' && specific.endsWith(' Bar') && n.includes(normalize(specific))))) found.push(c);
  }
  for (const [alias, canonical] of Object.entries(ALIASES)) {
    if (n.includes(normalize(alias)) && !found.includes(canonical)) found.push(canonical);
  }
  return found;
}

// The labels a restaurant should be filed under. Falls back to the owner's own
// wording so nothing is unfilterable, rather than dropping it into a catch-all
// bucket the visitor can't reach.
export function cuisineLabels(restaurant) {
  const canonical = canonicalCuisines(restaurant?.cuisine);
  if (canonical.length) return canonical;
  const raw = (restaurant?.cuisine || '').trim();
  return raw ? [raw] : [];
}

// Build the filter rail from what's actually listed, with counts, so an empty
// filter is visible before it's tapped.
export function cuisineFacets(restaurants) {
  const counts = new Map();
  for (const r of restaurants) {
    for (const label of cuisineLabels(r)) {
      counts.set(label, (counts.get(label) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    // Commonest first so the chips a visitor is most likely to want are
    // reachable without scrolling the rail; alphabetical breaks ties.
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function matchesCuisineFilter(restaurant, selected) {
  if (!selected || selected.size === 0) return true;
  return cuisineLabels(restaurant).some(label => selected.has(label));
}
